/**
 * Canonical PMI/MIP rate lookup — single source of truth for all calculators.
 *
 * Returns ANNUAL rates (e.g. 0.0062 = 0.62%).
 * Consumers convert to monthly rate (÷12) or monthly dollars (×loanAmount÷12) as needed.
 *
 * Matrix: 4 LTV tiers × 8 credit-score brackets for conventional loans.
 * FHA MIP: 0.55% (2023 rate for all LTVs).
 * USDA annual guarantee fee: 0.35%.
 * VA: 0 (no monthly PMI).
 */

export type PMILoanType = 'conventional' | 'fha' | 'va' | 'usda';

// ── Conventional PMI rate matrix ────────────────────────────────────────
// Rows = credit-score floors (descending). Columns = LTV tier.

interface RateRow {
  minScore: number;
  ltv85: number;   // DP ≥ 15%
  ltv90: number;   // 10% ≤ DP < 15%
  ltv95: number;   // 5% ≤ DP < 10%
  ltv97: number;   // DP < 5%
}

const CONVENTIONAL_PMI_MATRIX: RateRow[] = [
  { minScore: 760, ltv85: 0.0019, ltv90: 0.0027, ltv95: 0.0062, ltv97: 0.0075 },
  { minScore: 740, ltv85: 0.0022, ltv90: 0.0031, ltv95: 0.0065, ltv97: 0.0086 },
  { minScore: 720, ltv85: 0.0025, ltv90: 0.0037, ltv95: 0.0071, ltv97: 0.0098 },
  { minScore: 700, ltv85: 0.0027, ltv90: 0.0040, ltv95: 0.0082, ltv97: 0.0109 },
  { minScore: 680, ltv85: 0.0032, ltv90: 0.0048, ltv95: 0.0099, ltv97: 0.0125 },
  { minScore: 660, ltv85: 0.0038, ltv90: 0.0055, ltv95: 0.0118, ltv97: 0.0143 },
  { minScore: 640, ltv85: 0.0048, ltv90: 0.0070, ltv95: 0.0143, ltv97: 0.0175 },
  { minScore: 0,   ltv85: 0.0060, ltv90: 0.0090, ltv95: 0.0175, ltv97: 0.0200 },
];

const FHA_MIP_ANNUAL = 0.0055;   // 0.55%
const USDA_FEE_ANNUAL = 0.0035;  // 0.35%

// ── Public API ──────────────────────────────────────────────────────────

/**
 * Get the annual PMI/MIP rate for a given loan profile.
 *
 * @param loanType        – conventional | fha | va | usda
 * @param creditScore     – borrower FICO (e.g. 740)
 * @param downPaymentPct  – down-payment as a percentage of purchase price (e.g. 10 = 10%)
 * @returns annual rate (e.g. 0.0062). Multiply by loanAmount/12 for monthly $.
 */
export const getAnnualPMIRate = (
  loanType: PMILoanType | string,
  creditScore: number,
  downPaymentPct: number,
): number => {
  // VA: no monthly mortgage insurance
  if (loanType === 'va') return 0;

  // Conventional: no PMI at 20%+ down
  if (loanType === 'conventional' && downPaymentPct >= 20) return 0;

  // FHA MIP (simplified — single annual rate for all LTVs, per 2023 change)
  if (loanType === 'fha') return FHA_MIP_ANNUAL;

  // USDA annual guarantee fee
  if (loanType === 'usda') return USDA_FEE_ANNUAL;

  // ── Conventional matrix lookup ────────────────────────────────────────
  // Determine LTV tier
  const ltvTierKey = getLtvTierKey(downPaymentPct);

  // Find matching credit-score row (matrix is sorted descending by minScore)
  for (const row of CONVENTIONAL_PMI_MATRIX) {
    if (creditScore >= row.minScore) {
      return row[ltvTierKey];
    }
  }

  // Fallback (should never reach — last row has minScore 0)
  return CONVENTIONAL_PMI_MATRIX[CONVENTIONAL_PMI_MATRIX.length - 1][ltvTierKey];
};

/**
 * Convenience: monthly PMI dollar amount.
 */
export const getMonthlyPMIAmount = (
  loanAmount: number,
  loanType: PMILoanType | string,
  creditScore: number,
  downPaymentPct: number,
): number => {
  const annualRate = getAnnualPMIRate(loanType, creditScore, downPaymentPct);
  return (loanAmount * annualRate) / 12;
};

// ── Helpers ─────────────────────────────────────────────────────────────

type LtvTierKey = 'ltv85' | 'ltv90' | 'ltv95' | 'ltv97';

const getLtvTierKey = (downPaymentPct: number): LtvTierKey => {
  if (downPaymentPct >= 15) return 'ltv85';
  if (downPaymentPct >= 10) return 'ltv90';
  if (downPaymentPct >= 5) return 'ltv95';
  return 'ltv97';
};
